<template>
  <div id="app">
    <AppSidebar />
    <main :class="mainClass">
      <router-view :key="$route.fullPath" />
      <AppFooter v-if="showFooter" />
    </main>
    <ErrorModal />
    <ConfirmModal />

    <!-- 未登录时右上角登录（与侧栏退出后一致，登录后隐藏） -->
    <button
      v-if="!isLoggedIn"
      type="button"
      class="app-header-login-btn"
      @click="handleTopLoginClick"
    >{{ $t('nav.login') }}</button>
  </div>
</template>

<script>
import AppSidebar from './components/AppSidebar.vue'
import AppFooter from './components/AppFooter.vue'
import ErrorModal from './components/ErrorModal.vue'
import ConfirmModal from './components/ConfirmModal.vue'
import GA from '@/utils/ga'

var FOOTER_IMAGE_MODEL_SLUGS = {
  'flux-1-ai-image-generator': true,
  'flux-2-ai-image-generator': true,
  'seedream-ai-image-generator': true,
  'qwen-image-2-0-pro-ai-image-generator': true,
  'stable-diffusion-ai-image-generator': true,
  'z-image-turbo-ai-image-generator': true,
  'midjourney-ai-image-generator': true,
  'gpt-image-ai-image-generator': true
}

var FOOTER_ROUTE_NAMES = {
  Home: true,
  WatermarkRemoverDetail: true,
  DocCropEnhanceDetail: true,
  HandwrittenEraseDetail: true,
  ObjectRemoverDetail: true,
  FaceSwapDetail: true,
  Veo31VideoModelSeo: true,
  Sora2VideoModelSeo: true,
  Hub: true,
  HubCategory: true,
  HubDetail: true
}

export default {
  name: 'App',
  components: { AppSidebar, AppFooter, ErrorModal, ConfirmModal },
  data() {
    return { isMobile: false }
  },
  mounted() {
    var self = this
    self.isMobile = window.innerWidth <= 1024
    window.addEventListener('resize', function() { self.isMobile = window.innerWidth <= 1024 })

    self._syncUserInfoFromStorage()
    self._onWindowFocus = function () { self._syncUserInfoFromStorage() }
    self._onPageShow = function () { self._syncUserInfoFromStorage() }
    self._onVisibilityChange = function () {
      if (!document.hidden) self._syncUserInfoFromStorage()
    }
    window.addEventListener('focus', self._onWindowFocus)
    window.addEventListener('pageshow', self._onPageShow)
    document.addEventListener('visibilitychange', self._onVisibilityChange)

    /* 从 localStorage 恢复 pending 状态（覆盖刷新场景） */
    self._restorePendingFromStorage()
  },
  beforeDestroy: function () {
    window.removeEventListener('focus', this._onWindowFocus)
    window.removeEventListener('pageshow', this._onPageShow)
    document.removeEventListener('visibilitychange', this._onVisibilityChange)
  },
  metaInfo: function() {
    // 引用 currentLocale 使 vue-meta 能响应语言变化
    var locale = this.currentLocale
    var route = this.$route
    var meta = route.meta || {}
    var title = this.$t(meta.titleKey || 'seo.default.title')
    var description = this.$t(meta.descKey || 'seo.default.description')
    var lang = route.params.lang || locale
    // canonical 使用当前完整路径
    var canonicalUrl = 'https://www.miralvis.com' + (route.path === '' ? '/' : route.path)
    // 提取内容路径（去掉语言前缀）用于 hreflang
    var pathWithoutLang = route.path.replace(/^\/(zh|en|ja|ko|fr|es|it|de|pt|pl)(\/|$)/, '/') || '/'
    pathWithoutLang = pathWithoutLang.replace(/\/$/, '') || '/'

    // 生成 hreflang 标签
    var supportedLocales = ['zh', 'en', 'ja', 'ko', 'fr', 'es', 'it', 'de', 'pt', 'pl', 'ar']
    var hreflangLinks = supportedLocales.map(function(loc) {
      if (loc === 'en') {
        return { rel: 'alternate', hreflang: loc, href: 'https://www.miralvis.com' + pathWithoutLang }
      }
      return { rel: 'alternate', hreflang: loc, href: 'https://www.miralvis.com/' + loc + pathWithoutLang }
    })
    // 添加 x-default（默认语言，指向无前缀路径）
    hreflangLinks.push({ rel: 'alternate', hreflang: 'x-default', href: 'https://www.miralvis.com' + pathWithoutLang })

    return {
      title: title,
      meta: [
        { vmid: 'robots', name: 'robots', content: 'index, follow' },
        { vmid: 'googlebot', name: 'googlebot', content: 'max-video-preview:-1, max-image-preview:large, max-snippet:-1' },
        { vmid: 'description', name: 'description', content: description },
        { vmid: 'og:url', property: 'og:url', content: canonicalUrl },
        { vmid: 'og:site_name', property: 'og:site_name', content: 'Miral AI' },
        { vmid: 'og:locale', property: 'og:locale', content: lang },
        { vmid: 'og:type', property: 'og:type', content: 'website' },
        { vmid: 'og:title', property: 'og:title', content: title },
        { vmid: 'og:description', property: 'og:description', content: description },
        { vmid: 'og:image', property: 'og:image', content: 'https://www.miralvis.com/Miral.webp' },
        { vmid: 'twitter:site', name: 'twitter:site', content: '@MiralAI' },
        { vmid: 'twitter:card', name: 'twitter:card', content: 'summary_large_image' },
        { vmid: 'twitter:title', name: 'twitter:title', content: title },
        { vmid: 'twitter:description', name: 'twitter:description', content: description },
        { vmid: 'twitter:image', name: 'twitter:image', content: 'https://www.miralvis.com/Miral.webp' }
      ],
      link: [
        { rel: 'canonical', href: canonicalUrl }
      ].concat(hreflangLinks),
      htmlAttrs: {
        lang: lang
      }
    }
  },
  computed: {
    currentLocale: function() {
      return this.$i18n.locale
    },
    mainClass: function() {
      var cls = {}
      if (!this.isMobile) {
        cls['with-sidebar'] = true
      }
      return cls
    },
    isLoggedIn: function () {
      return !!this.$store.state.userInfo
    },
    showFooter: function () {
      var route = this.$route || {}
      if (FOOTER_ROUTE_NAMES[route.name]) return true
      if (route.name === 'ImageModelSeo') {
        return !!FOOTER_IMAGE_MODEL_SLUGS[route.params && route.params.slug]
      }
      return false
    }
  },
  methods: {
    handleTopLoginClick: function () {
      GA.loginClick()
      this.$store.dispatch('showLoginModal')
    },
    _restorePendingFromStorage: function () {
      try {
        var raw = localStorage.getItem('pending_generation')
        if (!raw) return
        var data = JSON.parse(raw)
        if (data && data.taskId) {
          this.$store.commit('SET_PENDING_GENERATION', data)
        }
      } catch (e) { /* ignore */ }
    },
    _syncUserInfoFromStorage: function () {
      try {
        var raw = localStorage.getItem('userInfo')
        if (!raw) {
          this.$store.commit('SET_USER_INFO', null)
          return
        }
        var parsed = JSON.parse(raw)
        this.$store.commit('SET_USER_INFO', parsed || null)
      } catch (e) {
        this.$store.commit('SET_USER_INFO', null)
      }
    }
  },
  watch: {
    '$i18n.locale': function() {
      this.$nextTick(function() {
        this.$root.$meta().refresh()
      })
    },
    '$route': {
      handler: function(to, from) {
        if (from && to.path !== from.path) {
          window.scrollTo(0, 0)
        }
      },
      immediate: true
    }
  }
}
</script>

<style>
#app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  width: 100%;
}
main {
  flex: 1;
  transition: margin-left 0.3s ease;
}
main.with-sidebar {
  margin-left: 72px;
}
@media (max-width: 1024px) {
  main.with-sidebar {
    margin-left: 0;
  }
}

/* 右上角登录（与站点浅色背景协调，参考 pill 按钮） */
.app-header-login-btn {
  position: fixed;
  top: max(12px, env(safe-area-inset-top, 0px));
  right: calc(16px + 10px + 10px + env(safe-area-inset-right, 0px));
  z-index: 9998;
  height: 36px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--accent, #059669);
  border-radius: 12px;
  cursor: pointer;
  font-family: var(--font-family, 'Inter', -apple-system, BlinkMacSystemFont, sans-serif);
  font-size: 14px;
  font-weight: 600;
  line-height: 1.5em;
  color: #059669;
  background: rgba(5, 150, 105, 0.06);
  box-shadow: var(--shadow-sm, 0 1px 2px rgba(0, 0, 0, 0.08));
  transition: all 0.2s;
}
.app-header-login-btn:hover {
  color: #1a1a2e;
  border-color: #ccc;
  background: #fff;
}
.app-header-login-btn:active {
  color: #059669;
  border-color: #059669;
  background: rgba(5, 150, 105, 0.06);
}

</style>
